/**
 * useBlogAnalytics.js
 *
 * Centralised analytics hook for the Blog section.
 * Mirrors the exact pattern of useYogAnalytics.js:
 *   - IS_PRODUCTION guard excludes localhost + 127.0.0.1
 *   - Dev mode: console.group + console.table with [BlogAnalytics] prefix
 *   - react-ga4 used in production
 *   - Debounced search query tracking (600ms)
 *
 * HOW TO USE:
 *   const analytics = useBlogAnalytics();
 *   analytics.trackCardClick(post);
 *
 * DEV MODE:
 *   Open browser DevTools → Console to see all events grouped under
 *   [BlogAnalytics] in real time. No GA4 dashboard needed during dev.
 */

import { useCallback, useRef } from "react";
import ReactGA from "react-ga4";

// ─── Production guard ──────────────────────────────────────────────────────────
// Mirrors useYogAnalytics: excludes localhost AND 127.0.0.1
const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

// ─── Core send helper ──────────────────────────────────────────────────────────
const send = (eventName, params = {}) => {
  // Strip undefined / null / empty values — keeps GA4 payload clean
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // Dev: grouped, coloured, table-formatted — mirrors [YogAnalytics] style
    // eslint-disable-next-line no-console
    console.group(`%c[BlogAnalytics] ${eventName}`, "color:#6C47FF;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

// ─── Simple debounce (no lodash dependency) ────────────────────────────────────
const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════

const useBlogAnalytics = () => {

  // Debounced search — 600ms matches useYogAnalytics
  const debouncedSearchRef = useRef(
    debounce((query, resultsCount) => {
      if (!query.trim()) return;
      send("blog_search_query", {
        search_term:   query,
        results_count: resultsCount,
      });
      if (resultsCount === 0) {
        send("blog_search_no_results", { search_term: query });
      }
    }, 600)
  );

  // ── Page views ───────────────────────────────────────────────────────────────

  const trackPageView = useCallback((title, url) => {
    send("blog_page_view", {
      page_title:    title,
      page_location: url,
    });
  }, []);

  // ── Listing page ─────────────────────────────────────────────────────────────

  const trackCategoryFilter = useCallback((category) => {
    send("blog_category_filter", {
      category_selected: category ?? "all",
      event_label:       category ?? "All Posts",
    });
  }, []);

  const trackCardClick = useCallback((post, isFeatured = false) => {
    send("blog_card_click", {
      post_id:       String(post.sN),
      post_title:    post.title,
      post_category: post.category,
      post_author:   post.author,
      is_featured:   isFeatured,
    });
  }, []);

  // ── Article page ─────────────────────────────────────────────────────────────

  const trackArticleView = useCallback((post) => {
    send("blog_article_view", {
      post_id:       String(post.sN),
      post_title:    post.title,
      post_category: post.category,
      post_author:   post.author,
      post_date:     post.date,
    });
  }, []);

  const trackScrollDepth = useCallback((depth, post) => {
    send("blog_scroll_depth", {
      scroll_depth:  depth,
      event_label:   `${depth}%`,
      post_id:       String(post.sN),
      post_title:    post.title,
      post_category: post.category,
    });
  }, []);

  const trackTimeOnPage = useCallback((seconds, post) => {
    send("blog_time_on_page", {
      seconds,
      event_label:   `${seconds}s`,
      post_id:       String(post.sN),
      post_title:    post.title,
      post_category: post.category,
    });
  }, []);

  const trackPageExit = useCallback((totalSeconds, post) => {
    send("blog_page_exit", {
      time_spent_seconds: totalSeconds,
      event_label:        `${totalSeconds}s`,
      post_id:            String(post.sN),
      post_title:         post.title,
      post_category:      post.category,
    });
  }, []);

  const trackVideoLoad = useCallback((videoId, caption, post, blockIndex) => {
    send("blog_video_load", {
      video_id:      videoId,
      video_caption: caption,
      post_id:       String(post.sN),
      post_title:    post.title,
      block_index:   blockIndex,
    });
  }, []);

  const trackImageView = useCallback((src, alt, post, blockIndex) => {
    send("blog_image_view", {
      image_src:   src,
      image_alt:   alt,
      post_id:     String(post.sN),
      post_title:  post.title,
      block_index: blockIndex,
    });
  }, []);

  const trackOutboundLink = useCallback((href, text, post) => {
    send("blog_outbound_link_click", {
      link_url:   href,
      link_text:  text,
      post_id:    String(post.sN),
      post_title: post.title,
    });
  }, []);

  const trackTagClick = useCallback((tag, post) => {
    send("blog_tag_click", {
      tag_name:   tag,
      post_id:    String(post.sN),
      post_title: post.title,
    });
  }, []);

  const trackBackClick = useCallback((post) => {
    send("blog_back_to_listing", {
      post_id:    String(post?.sN ?? ""),
      post_title: post?.title ?? "unknown",
    });
  }, []);

  // ── Search ───────────────────────────────────────────────────────────────────

  const trackSearchQuery = useCallback((query, resultsCount) => {
    debouncedSearchRef.current(query, resultsCount);
  }, []);

  const trackSearchClear = useCallback(() => {
    send("blog_search_clear");
  }, []);

  return {
    trackPageView,
    trackCategoryFilter,
    trackCardClick,
    trackArticleView,
    trackScrollDepth,
    trackTimeOnPage,
    trackPageExit,
    trackVideoLoad,
    trackImageView,
    trackOutboundLink,
    trackTagClick,
    trackBackClick,
    trackSearchQuery,
    trackSearchClear,
  };
};

export default useBlogAnalytics;
